/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'

import { useGetProjectQuery } from '@modules/project-admin-lib/hooks/get'
import {
  useListElasticsearchProjectsQuery as useAdminListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery as useAdminListObservabilityProjectsQuery,
  useListSecurityProjectsQuery as useAdminListSecurityProjectsQuery,
  useGetElasticsearchLinkCandidatesQuery,
  useGetObservabilityLinkCandidatesQuery,
  useGetSecurityLinkCandidatesQuery,
} from '@modules/project-admin-lib/hooks/list'
import { getPatchProjectMutationHook } from '@modules/project-admin-lib/hooks/update'
import { useListRegionsQuery } from '@modules/cluster-admin-lib/hooks'
import type { ProjectID, ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'
import type { QueryHookOptions } from '@modules/query/types'

import { ProjectCpsProvider } from '../ProjectCpsContext'

import type { ProjectCpsContextValue, UseListProjectsQueryHook } from '../ProjectCpsContext'

type Props = {
  /**
   * Organization ID to filter projects by.
   * Required for admin console to scope project lists.
   */
  organizationId: string
  children: React.ReactNode
}

/**
 * Hook that fetches link candidates based on project type.
 * Calls the appropriate underlying hook based on the project type.
 */
const useGetLinkCandidatesQuery = (
  projectType: ProjectTypeWithWorkplaceAI,
  projectId: ProjectID,
  options?: QueryHookOptions,
) => {
  const elasticsearchQuery = useGetElasticsearchLinkCandidatesQuery(projectId, {
    ...options,
    enabled: projectType === 'elasticsearch' && options?.enabled !== false,
  })

  const observabilityQuery = useGetObservabilityLinkCandidatesQuery(projectId, {
    ...options,
    enabled: projectType === 'observability' && options?.enabled !== false,
  })

  const securityQuery = useGetSecurityLinkCandidatesQuery(projectId, {
    ...options,
    enabled: projectType === 'security' && options?.enabled !== false,
  })

  if (projectType === 'elasticsearch') {
    return elasticsearchQuery
  }

  if (projectType === 'observability') {
    return observabilityQuery
  }

  return securityQuery
}

/**
 * Provider for Admin Console that supplies admin-api hooks to ProjectCps components.
 * Wraps the admin hooks to match the expected interface.
 */
const AdminConsoleProvider: React.FunctionComponent<Props> = ({ organizationId, children }) => {
  const contextValue: ProjectCpsContextValue = useMemo(() => {
    // Create adapter hooks that match the user console interface
    // but use admin API with the provided organization_id
    const useListElasticsearchProjectsQuery: UseListProjectsQueryHook = (queryParams, options?) =>
      useAdminListElasticsearchProjectsQuery(
        {
          organization_id: organizationId,
          include_deleted: queryParams?.include_deleted,
          linked: queryParams?.linked,
        },
        options,
      )

    const useListObservabilityProjectsQuery: UseListProjectsQueryHook = (queryParams, options?) =>
      useAdminListObservabilityProjectsQuery(
        {
          organization_id: organizationId,
          include_deleted: queryParams?.include_deleted,
          linked: queryParams?.linked,
        },
        options,
      )

    const useListSecurityProjectsQuery: UseListProjectsQueryHook = (queryParams, options?) =>
      useAdminListSecurityProjectsQuery(
        {
          organization_id: organizationId,
          include_deleted: queryParams?.include_deleted,
          linked: queryParams?.linked,
        },
        options,
      )

    // Create adapter hooks for mutations using the factory function
    const usePatchElasticsearchProjectMutation = () =>
      getPatchProjectMutationHook('elasticsearch')()
    const usePatchObservabilityProjectMutation = () =>
      getPatchProjectMutationHook('observability')()
    const usePatchSecurityProjectMutation = () => getPatchProjectMutationHook('security')()

    return {
      useGetProjectQuery,
      useListElasticsearchProjectsQuery,
      useListObservabilityProjectsQuery,
      useListSecurityProjectsQuery,
      usePatchElasticsearchProjectMutation,
      usePatchObservabilityProjectMutation,
      usePatchSecurityProjectMutation,
      useListRegionsQuery,
      useGetLinkCandidatesQuery,
    }
  }, [organizationId])

  return <ProjectCpsProvider value={contextValue}>{children}</ProjectCpsProvider>
}

export default AdminConsoleProvider
